/**
 * Resolves the configuration the standalone build needs in order to talk to
 * NerdGraph. Standalone-only: none of this is copied into the nerdpack.
 *
 * Precedence (lowest to highest):
 *   1. defaults below
 *   2. build-time VITE_* env vars
 *   3. config.json fetched next to index.html  <- change without rebuilding
 *   4. localStorage                            <- per-browser overrides
 */

const STORAGE_KEY = "healthDashboard.nr1Config";

const DEFAULTS = {
  accountId: null,
  region: "US",

  // Same-origin path. `vite dev` proxies it (see vite.config.js) and in
  // production a CloudFront behaviour routes it to the proxy Lambda, so the
  // browser never makes a cross-origin request and never sees the API key.
  nerdgraphEndpoint: "/api/nerdgraph",

  // Origin of the operational-health ("RMP") API behind the market/store/trend
  // views (src/nerdlets/home/useApi.js et al.). Overridable via config.json,
  // VITE_HEALTH_API_BASE or localStorage so the standalone build can be
  // repointed without a rebuild; the nerdpack always uses its compiled default.
  healthApiBase:
    "https://xbzpe5g7i2ibwqyuwlw2uszjva0zhtqq.lambda-url.us-east-1.on.aws",

  // "proxy"  -> nerdgraphEndpoint is your backend; it attaches credentials.
  // "apiKey" -> the browser sends an API-Key header itself. Note that public
  //             NerdGraph does not answer CORS preflights, so this only works
  //             against an endpoint that does.
  authMode: "proxy",

  apiKey: null,

  // Stands in for the signed-in New Relic user that UserQuery would resolve.
  // Once Cognito sign-in succeeds this is replaced by the real account.
  userEmail: null,
  userName: null,

  // Cognito sign-in for the standalone build. Both values are public
  // identifiers, safe to ship in config.json; the app client must have no
  // client secret (see src/auth/cognito.js).
  cognito: { userPoolId: null, clientId: null },

  // Fail closed: if sign-in is switched on but misconfigured, show the
  // landing page rather than silently exposing the dashboard.
  requireAuth: true,

  // Maps a service entity type (the TYPE segment of an EXT guid) to the New
  // Relic dashboard that backs its entity view. Editable on S3 without a
  // rebuild, so a service can be repointed at a different dashboard.
  entityDashboards: {},

  timeRangeDurationMs: 30 * 60 * 1000,
};

const asAccountId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

function fromEnv() {
  const env = import.meta.env || {};
  return prune({
    accountId: asAccountId(env.VITE_NR_ACCOUNT_ID),
    region: env.VITE_NR_REGION,
    nerdgraphEndpoint: env.VITE_NERDGRAPH_ENDPOINT,
    authMode: env.VITE_NR_AUTH_MODE,
    apiKey: env.VITE_NR_API_KEY,
    healthApiBase: env.VITE_HEALTH_API_BASE,
  });
}

async function fromConfigFile() {
  try {
    const url = new URL("config.json", document.baseURI);
    const response = await fetch(url.toString(), { cache: "no-store" });
    if (!response.ok) return {};
    const parsed = await response.json();
    return prune({
      accountId: asAccountId(parsed.accountId),
      region: parsed.region,
      nerdgraphEndpoint: parsed.nerdgraphEndpoint,
      healthApiBase: parsed.healthApiBase,
      authMode: parsed.authMode,
      userEmail: parsed.userEmail,
      userName: parsed.userName,
      cognito: parsed.cognito,
      requireAuth: parsed.requireAuth,
      entityDashboards: parsed.entityDashboards,
      timeRangeDurationMs: parsed.timeRangeDurationMs,
    });
  } catch {
    // A missing or malformed config.json falls back to the defaults above.
    return {};
  }
}

/**
 * Per-browser escape hatch for debugging a deployed build, e.g.
 *   localStorage.setItem("healthDashboard.nr1Config",
 *     JSON.stringify({ accountId: 3547573 }))
 */
function readStoredOverrides() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return prune({
      accountId: asAccountId(parsed.accountId),
      apiKey: parsed.apiKey,
      healthApiBase: parsed.healthApiBase,
      userEmail: parsed.userEmail,
      // Lets a developer skip the sign-in gate locally without editing the
      // shipped config. This weakens nothing that was protecting anything: the
      // gate guards the UI, never the files (see src/auth/cognito.js).
      requireAuth:
        typeof parsed.requireAuth === "boolean" ? parsed.requireAuth : undefined,
    });
  } catch {
    return {};
  }
}

function prune(object) {
  return Object.fromEntries(
    Object.entries(object).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  );
}

export async function resolveConfig() {
  const merged = {
    ...DEFAULTS,
    ...fromEnv(),
    ...(await fromConfigFile()),
    ...readStoredOverrides(),
  };

  return {
    ...merged,
    timeRange: {
      begin_time: null,
      end_time: null,
      duration: merged.timeRangeDurationMs,
    },
  };
}
